import { motion } from "motion/react";
import { bool, object } from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components/macro";
import { opacity } from "theme";

import {
  useHasSubscribedGroups,
  useParentKeytoChildGroups,
} from "GroupsManagement/hooks/useNavBarGroups";
import LinkListRow, {
  linkListRowPaddingX,
} from "NavBar/components/LinkListRow";
import { menuTypographyProps } from "NavBar/components/NavLink";
import { navSectionKeys } from "NavBar/navBarStore";
import { listIconProps } from "NavBar/styleConstants";

import { FnrShineButtonWrapper } from "UI/FnrButton3/FnrShineButton";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrNetworkPhoto from "UI/FnrNetworkPhoto";
import FnrOpacityToggle from "UI/FnrOpacityToggle";
import FnrTypography from "UI/FnrTypography";

import useHeight from "Utilities/Hooks/useHeight";
import useIsHoverFocused, {
  useIsFocusVisible,
} from "Utilities/Hooks/useIsHoverFocused";
import dynHeight from "Utilities/interpolations/dynHeight";
import dynMarginBottom from "Utilities/interpolations/dynMarginBottom";
import dynMarginLeft from "Utilities/interpolations/dynMarginLeft";
import dynMarginTop from "Utilities/interpolations/dynMarginTop";

import { useDebouncedSearchValue } from "../groupsCommunicationStore";

import { useIsSearchHitByGroupID } from "./stateHooks";

/**
 * @param {import("react").MutableRefObject<number>} timerRef
 * @param {{}} timerResetTrigger
 * @param {Group} group
 * @returns {JSX.Element|null}
 */
const RecursiveGroups = ({
  timerRef,
  timerResetTrigger,
  isInitialLoad,
  ...group
}) => {
  const debouncedSearchValue = useDebouncedSearchValue();
  const { parentKeyToChildGroups } = useParentKeytoChildGroups();
  const { hasSubscribedGroups } = useHasSubscribedGroups();
  const isSearchHitByGroupID = useIsSearchHitByGroupID();

  const getHasSearchHitInSubtree = useCallback(
    (groupID) => {
      const isMatch = isSearchHitByGroupID[groupID];
      if (typeof isMatch !== "boolean") {
        console.warn(
          "Unable to determine if group matches search term, groupID: ",
          groupID
        );
        return false;
      }
      const _childGroups = parentKeyToChildGroups[groupID];

      if (!Array.isArray(_childGroups) || _childGroups.length === 0) {
        return isMatch;
      }

      return _childGroups.reduce(
        (_isMatch, childGroup) =>
          _isMatch || getHasSearchHitInSubtree(childGroup.ID),
        isMatch
      );
    },
    [isSearchHitByGroupID, parentKeyToChildGroups]
  );

  const hasSearchHitInSubtree = useMemo(
    () => getHasSearchHitInSubtree(group.ID),
    [getHasSearchHitInSubtree, group.ID]
  );

  const pendingInDom = useMemo(() => {
    if (debouncedSearchValue.length > 0) return hasSearchHitInSubtree;

    // Show all groups if user has no subscribed groups,
    // or if the specific group is subscribed
    return !hasSubscribedGroups || group.subscribed;
  }, [
    group.subscribed,
    hasSearchHitInSubtree,
    hasSubscribedGroups,
    debouncedSearchValue.length,
  ]);

  const [show, setShow] = useState(isInitialLoad && pendingInDom);
  const [inDom, setInDom] = useState(isInitialLoad && pendingInDom);
  const timeoutRef = useRef(/** @type {number | undefined} */ undefined);
  const hasAnimatedRef = useRef(isInitialLoad);
  useEffect(() => {
    // Skip stagger on initial data load — show groups immediately
    if (hasAnimatedRef.current) {
      hasAnimatedRef.current = false;
      return;
    }
    clearTimeout(timeoutRef.current);
    if (pendingInDom) {
      timerRef.current += 20;
      timeoutRef.current = setTimeout(() => {
        setInDom(pendingInDom);
      }, timerRef.current);
    } else {
      setShow(false);
      setTimeout(() => setInDom(false), 200);
    }
  }, [pendingInDom, timerRef, timerResetTrigger]);

  useEffect(() => {
    if (inDom) setShow(inDom);
  }, [inDom]);

  const childGroups = useMemo(
    () => parentKeyToChildGroups[group.ID] || [],
    [group.ID, parentKeyToChildGroups]
  );

  /** @type DepthSpecificStyles */
  const depthSpecificStyles = (() => {
    const isTopLevel = !group.hierarchyLevel || group.hierarchyLevel === 1;

    if (isTopLevel) {
      return (
        DEPTH_SPECIFIC_STYLES[group.section] ??
        DEPTH_SPECIFIC_STYLES.orphanedParents
      );
    }

    return (
      DEPTH_SPECIFIC_STYLES[group.hierarchyLevel - 1] ??
      DEPTH_SPECIFIC_STYLES[1]
    );
  })();

  const visibilityIcon = useMemo(() => {
    switch (group.type.value) {
      case "hidden":
        return "eye-slash fad";
      case "closed":
        return "lock fad";
      default:
        return "";
    }
  }, [group.type.value]);

  const visibilityTitle = useMemo(() => {
    switch (group.type.value) {
      case "hidden":
        return "Hidden Group";
      case "closed":
        return "Closed Group";
      default:
        return "";
    }
  }, [group.type.value]);

  // Check if any child groups will actually be visible (not just exist in data)
  const hasVisibleChildGroups = useMemo(() => {
    if (childGroups.length === 0) return false;

    return childGroups.some((child) => {
      if (debouncedSearchValue.length > 0) {
        return getHasSearchHitInSubtree(child.ID);
      }
      return !hasSubscribedGroups || child.subscribed;
    });
  }, [
    childGroups,
    debouncedSearchValue.length,
    hasSubscribedGroups,
    getHasSearchHitInSubtree,
  ]);

  const [areChildGroupsCollapsed, setAreChildGroupsCollapsed] = useState(
    !hasVisibleChildGroups
  );

  // Auto-expand when search reveals children or when searching with matching children
  // This ensures search results are always visible, even if user manually collapsed
  useEffect(() => {
    if (hasVisibleChildGroups && debouncedSearchValue.length > 0) {
      setAreChildGroupsCollapsed(false);
    }
  }, [hasVisibleChildGroups, debouncedSearchValue.length]);

  // Also expand when children become visible for the first time
  useEffect(() => {
    if (hasVisibleChildGroups && debouncedSearchValue.length === 0) {
      setAreChildGroupsCollapsed(false);
    }
    // Only run when hasVisibleChildGroups changes, not on every searchValue change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVisibleChildGroups]);

  const childGroupsWrapperRef = useRef(
    /** @type {HTMLDivElement | undefined} */ undefined
  );
  function toggleChildGroupsCollapse() {
    setAreChildGroupsCollapsed((prev) => !prev);
    setTimeout(() => {
      // It turned out that comparing against the dom element was more reliable
      // than trying to close over the current or next value of `areChildGroupsCollapsed`

      if (!childGroupsWrapperRef.current) return;

      if (
        window.getComputedStyle(childGroupsWrapperRef.current).visibility ===
        "visible"
      ) {
        childGroupsWrapperRef.current.scrollIntoView({ block: "nearest" });
      }
    }, 350);
  }

  const groupRowRef = useRef();
  const isGroupRowHoverFocused = useIsHoverFocused(groupRowRef);
  const isFocusVisible = useIsFocusVisible(groupRowRef);
  const shouldShowCollapseToggle =
    hasVisibleChildGroups &&
    !!(!areChildGroupsCollapsed || isGroupRowHoverFocused);
  const shouldShowIcons = !!(isGroupRowHoverFocused || isFocusVisible);

  const contentRef = useRef();
  const contentHeight = useHeight(contentRef);

  if (!inDom) return null;

  return (
    <>
      <GroupRowWrapper
        ref={groupRowRef}
        $marginTop={show ? 1.5 + (depthSpecificStyles.marginTop ?? 0) : 0}
        $marginBottom={show ? 1.5 : 0}
      >
        <SLinkListRow
          $marginLeft={depthSpecificStyles.marginLeft}
          to={`/${group.shortname}`}
          navSectionKey={navSectionKeys.groups}
          $variant={show ? "shown" : "hidden"}
          $height={show ? contentHeight + linkListRowPaddingX * 2 : 0}
          contentRef={contentRef}
          tapScale={0.97}
          disableWhenSelected
          $scale
        >
          <PhotoAndNameWrapper>
            {Boolean(depthSpecificStyles.iconSize) && (
              <FnrNetworkPhoto
                src={group.photoURL}
                noBorder
                size={depthSpecificStyles.iconSize}
              />
            )}
            <FnrTypography
              {...menuTypographyProps}
              {...(depthSpecificStyles.shouldStyleAsSubheader
                ? { type: "subheader", xMarginAdjust: 4 }
                : {})}
              fontSize={depthSpecificStyles.fontSize}
              noWrap={false}
            >
              {group.name}
            </FnrTypography>
          </PhotoAndNameWrapper>
          <IconsWrapper>
            <FnrOpacityToggle
              show={group.isSmartGroup && group.isGroupAdmin && shouldShowIcons}
            >
              <FnrContainedIcon
                {...listIconProps}
                icon="code-branch fad"
                title="Smart Group"
              />
            </FnrOpacityToggle>
            <FnrOpacityToggle
              show={Boolean(group.includeContacts) && group.isGroupAdmin && shouldShowIcons}
            >
              {group.includeContacts && (
                <FnrContainedIcon
                  {...listIconProps}
                  icon="mail-bulk fad"
                  title="Includes All Contacts"
                />
              )}
            </FnrOpacityToggle>
            <FnrOpacityToggle show={Boolean(visibilityIcon) && group.isGroupAdmin && shouldShowIcons}>
              <FnrContainedIcon
                {...listIconProps}
                icon={visibilityIcon}
                title={visibilityTitle}
              />
            </FnrOpacityToggle>
            {/* We do not place the toggle here to avoid invalid DOM nesting */}
            {/* i.e. we cannot place a <button> within a <button> */}
            {/* But we do use a placeholder for styling purposes */}
            <CollapseToggleButtonPlaceholder aria-hidden />
          </IconsWrapper>
        </SLinkListRow>
        <CollapseToggleButtonWrapper
          show={shouldShowCollapseToggle}
          aria-hidden={!shouldShowCollapseToggle}
        >
          {/* https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/ */}
          <CollapseToggleButton
            aria-controls={`child-groups-of-group-${group.ID}`}
            aria-expanded={!areChildGroupsCollapsed}
            className={!areChildGroupsCollapsed && "is-flipped"}
            onClick={toggleChildGroupsCollapse}
          >
            <FnrContainedIcon
              {...listIconProps}
              size="xs"
              color="dark"
              icon="angle-down fas"
              title={`${
                areChildGroupsCollapsed ? "Expand" : "Collapse"
              } Subgroups`}
            />
          </CollapseToggleButton>
        </CollapseToggleButtonWrapper>
      </GroupRowWrapper>
      {childGroups.length > 0 && (
        <ChildGroupsWrapper
          ref={childGroupsWrapperRef}
          id={`child-groups-of-group-${group.ID}`}
          animate={{
            scaleY: areChildGroupsCollapsed ? 0 : 1,
            translateY: areChildGroupsCollapsed ? "-6px" : 0,
            height: areChildGroupsCollapsed ? 0 : "auto",
            visibility: "inherit",
            transitionEnd: {
              visibility: areChildGroupsCollapsed ? "hidden" : "inherit",
            },
          }}
          transition={{ duration: 0.15 }}
        >
          {childGroups.map((childGroup) => (
            <RecursiveGroups
              timerRef={timerRef}
              isInitialLoad={isInitialLoad}
              key={childGroup.ID}
              {...childGroup}
            />
          ))}
        </ChildGroupsWrapper>
      )}
    </>
  );
};

RecursiveGroups.propTypes = {
  timerRef: object,
  timerResetTrigger: object,
  isInitialLoad: bool,
};

/**
 * @typedef {object} DepthSpecificStyles
 * @property {boolean | undefined} shouldStyleAsSubheader
 * @property {string} fontSize
 * @property {string | undefined} iconSize
 * @property {number} marginLeft
 * @property {number | undefined} marginTop
 */

/** @type {Readonly<Record<string|number, DepthSpecificStyles>>} */
const DEPTH_SPECIFIC_STYLES = Object.freeze({
  // Top-level groups WITH grandchildren - subheader styling (uppercase, larger)
  grandparents: {
    shouldStyleAsSubheader: true,
    fontSize: "lg",
    // icon is hidden
    marginLeft: 2,
    marginTop: 4,
  },
  // Top-level groups WITHOUT grandchildren - regular styling
  orphanedParents: {
    fontSize: "md",
    iconSize: "sm",
    marginLeft: 2,
  },
  // Children of grandparents (depth 1)
  1: {
    fontSize: "md",
    iconSize: "sm",
    marginLeft: 10,
  },
  // Grandchildren (depth 2)
  2: {
    fontSize: "sm",
    iconSize: "xs",
    marginLeft: 36,
  },
});

const GroupRowWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  ${dynMarginTop}
  ${dynMarginBottom}
  transition: margin 300ms;

  // When hovering the collapse toggle (inside this wrapper but outside the
  // shine button), preserve the shine hover appearance on the row button.
  &:hover ${FnrShineButtonWrapper}:not(:hover) > :first-child > :first-child {
    box-shadow: inset
      rgba(
        var(--fnrShineColorR),
        var(--fnrShineColorG),
        var(--fnrShineColorB),
        0.3
      )
      0 0 3px;
    transition: box-shadow 0.2s;
  }
  &:hover ${FnrShineButtonWrapper}:not(:hover) > :first-child > :nth-child(2) {
    opacity: 0.2;
    transition: opacity 0.4s, background 0.2s;
  }
`;

const SLinkListRow = styled(LinkListRow)`
  ${dynMarginLeft}
  ${opacity}
  ${dynHeight}
  transition: opacity 300ms, transform 300ms, height 300ms, margin 300ms;
  // Ensure collapsed child group links are not tab-focusable
  visibility: inherit;
`;

const PhotoAndNameWrapper = styled.div`
  margin-left: 2px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconsWrapper = styled.div`
  display: flex;
  gap: 4px;
`;

const COLLAPSE_TOGGLE_BUTTON_SIZE_IN_PX = 20;

const CollapseToggleButtonPlaceholder = styled.div`
  height: ${COLLAPSE_TOGGLE_BUTTON_SIZE_IN_PX}px;
  width: ${COLLAPSE_TOGGLE_BUTTON_SIZE_IN_PX}px;
`;

const CollapseToggleButtonWrapper = styled(FnrOpacityToggle)`
  // Overlap the CollapseToggleButtonPlaceholder
  position: absolute;
  right: 6px;
  top: calc(50% - ${COLLAPSE_TOGGLE_BUTTON_SIZE_IN_PX / 2}px);
`;

const CollapseToggleButton = styled.button`
  // reset
  all: unset;
  outline: revert;

  display: inline-block;
  height: ${COLLAPSE_TOGGLE_BUTTON_SIZE_IN_PX}px;
  width: ${COLLAPSE_TOGGLE_BUTTON_SIZE_IN_PX}px;
  border: none;
  border-radius: 9999px;
  padding: 0;
  background-color: rgba(255, 255, 255, 0.15);
  transition: background-color 50ms ease-in-out;
  cursor: pointer;

  &:not(:active):hover,
  &:not(:active):focus-visible {
    background-color: rgba(255, 255, 255, 0.9);
  }
  &:active {
    background-color: rgba(255, 255, 255, 0.7);
  }

  > * {
    transition: transform 250ms ease-in-out;
  }
  &:not(.is-flipped) > * {
    transform: translateY(-1px);
  }
  &.is-flipped > * {
    transform: rotateX(180deg);
  }
`;

const ChildGroupsWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  // Ensure we clear the FloatingButtonPanel
  scroll-margin-bottom: 60px;
`;

export default RecursiveGroups;
